import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Cloud,
  ChevronRight,
  Play,
} from "lucide-react";
import PageMeta from "../components/PageMeta";
import { LiveDashboardPulse, AIInsightCard } from "../components/VisualElements";
import { DEMO_URL } from "../lib/urls";

const SECTIONS = [
  {
    title: "Geminii Space",
    desc: "Advanced apps and websites for businesses that refuse to outgrow their software.",
    logo: "/logos/space/white-blue.png",
    to: "/space",
    border: "border-rogue-cyan/20",
    hoverBorder: "hover:border-rogue-cyan/40",
  },
  {
    title: "Geminii Nexus",
    desc: "Real-time cloud control for the operations that can't go offline. Sense anything, act anywhere.",
    logo: "/logos/nexus/white-blue.png",
    to: "/nexus",
    border: "border-nexus-blue/20",
    hoverBorder: "hover:border-nexus-blue/40",
  },
  {
    title: "Cerebrum",
    desc: "The AI brain of Geminii. Thinks alongside you across every product and surface.",
    logo: "/logos/cerebrum/white-blue.png",
    to: "/cerebrum",
    border: "border-cerebrum-violet/20",
    hoverBorder: "hover:border-cerebrum-violet/40",
  },
  {
    title: "Geminii Cloud",
    desc: "Cloud storage and infrastructure. Coming 2026.",
    logo: null,
    icon: Cloud,
    to: "/cloud",
    border: "border-white/[0.06]",
    hoverBorder: "hover:border-white/[0.12]",
    comingSoon: true,
  },
];

export default function HomePage({ onTrialClick }) {
  return (
    <div className="min-h-screen">
      <PageMeta
        title={null}
        description="Geminii — Software, hardware, intelligence. Built for the operators who run the real world. CRM, field service, AI, and more."
        path="/"
      />
      {/* Hero */}
      <section
        data-testid="home-hero"
        className="relative pt-28 md:pt-36 pb-12 md:pb-16 overflow-hidden"
      >
        {/* Background image */}
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `url(/images/hero-home.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-geminii-deep/30 via-geminii-deep/60 to-geminii-deep" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <img
                src="/logos/geminii/white-blue.png"
                alt="Geminii"
                className="h-14 sm:h-18 lg:h-22 w-auto"
                data-testid="home-hero-logo"
              />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
              className="mt-6 text-lg md:text-xl text-geminii-muted leading-relaxed max-w-2xl"
            >
              Software, hardware, intelligence — built for the operators who run the real world.
            </motion.p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={onTrialClick}
                data-testid="home-hero-cta"
                className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-rogue-cyan text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(31,182,193,0.35)] transition-all duration-250"
              >
                Start free trial
                <ArrowRight size={16} />
              </button>
              <Link
                to="/space/rogue"
                data-testid="home-hero-explore"
                className="flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-rogue-cyan/50 hover:text-rogue-cyan transition-all duration-250"
              >
                Explore Rogue
                <ChevronRight size={16} />
              </Link>
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-hero-demo"
                className="flex items-center gap-2 px-7 py-3.5 rounded-full border border-rogue-cyan/30 text-rogue-cyan font-semibold text-sm hover:bg-rogue-cyan/10 hover:border-rogue-cyan/60 transition-all duration-250"
              >
                <Play size={14} className="fill-rogue-cyan" />
                View live demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Product sections */}
      <section data-testid="home-products" className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-display font-bold text-2xl md:text-3xl text-white mb-3"
          >
            Everything Geminii
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-geminii-muted text-base md:text-lg mb-10 max-w-xl"
          >
            Four divisions. One mission: give operators the tools to run everything.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SECTIONS.map((section, i) => {
              const Card = (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  whileHover={{ scale: 1.02 }}
                  className={`group relative bg-geminii-card rounded-xl border ${section.border} ${section.hoverBorder} p-6 md:p-10 transition-all duration-250 hover:shadow-[0_0_30px_-5px_rgba(31,182,193,0.15)] cursor-pointer`}
                  data-testid={`home-card-${section.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {/* Mobile: logo top-left, chevron top-right, title+desc below full-width. md+: original row layout. */}
                  <div className="flex md:hidden items-center justify-between mb-4">
                    {section.logo ? (
                      <img src={section.logo} alt={section.title} className="h-6 w-auto" />
                    ) : (
                      <div className="p-2.5 rounded-lg bg-white/[0.04] text-geminii-dim">
                        <section.icon size={22} />
                      </div>
                    )}
                    <ChevronRight
                      size={18}
                      className="text-geminii-dim group-hover:text-white group-hover:translate-x-1 transition-all duration-250 flex-shrink-0"
                    />
                  </div>
                  <div className="block md:hidden">
                    <h3 className="font-display font-bold text-lg text-white mb-2 flex items-center flex-wrap gap-2">
                      {section.title}
                      {section.comingSoon && (
                        <span className="text-xs font-mono text-geminii-dim bg-white/[0.04] px-2 py-0.5 rounded">
                          2026
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-geminii-muted leading-relaxed">
                      {section.desc}
                    </p>
                  </div>

                  <div className="hidden md:flex items-start gap-4">
                    {section.logo ? (
                      <img src={section.logo} alt={section.title} className="h-7 w-auto mt-0.5" />
                    ) : (
                      <div className="p-3 rounded-lg bg-white/[0.04] text-geminii-dim">
                        <section.icon size={24} />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-display font-bold text-lg text-white mb-2 flex items-center flex-wrap gap-2">
                        {section.title}
                        {section.comingSoon && (
                          <span className="text-xs font-mono text-geminii-dim bg-white/[0.04] px-2 py-0.5 rounded">
                            2026
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-geminii-muted leading-relaxed">
                        {section.desc}
                      </p>
                    </div>
                    <ChevronRight
                      size={18}
                      className="text-geminii-dim group-hover:text-white group-hover:translate-x-1 transition-all duration-250 mt-1 flex-shrink-0"
                    />
                  </div>
                </motion.div>
              );

              if (section.external) {
                return (
                  <a
                    key={section.title}
                    href={NEXUS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {Card}
                  </a>
                );
              }
              return (
                <Link key={section.title} to={section.to}>
                  {Card}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Live Platform Pulse */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <LiveDashboardPulse />
            <AIInsightCard />
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section data-testid="home-trust" className="py-12 border-t border-b border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-xs text-geminii-dim uppercase tracking-[0.2em] font-medium mb-4">
            Trusted by operators worldwide
          </p>
          <p className="text-sm text-geminii-muted max-w-md mx-auto">
            From field service to full-scale operations — businesses run on Rogue every day.
          </p>
        </div>
      </section>

      {/* Closing CTA */}
      <section data-testid="home-closing-cta" className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display font-bold text-3xl md:text-4xl text-white mb-4"
          >
            The operator's choice. End to end.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-geminii-muted mb-10 max-w-md mx-auto"
          >
            From CRM to field service, hardware automation to AI — Geminii has it built.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <button
              onClick={onTrialClick}
              data-testid="home-closing-cta-btn"
              className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-rogue-cyan text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(31,182,193,0.35)] transition-all duration-250"
            >
              Start free trial
              <ArrowRight size={16} />
            </button>
            <Link
              to="/contact"
              className="flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-rogue-cyan/50 hover:text-rogue-cyan transition-all duration-250"
            >
              Contact us
            </Link>
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-closing-demo"
              className="flex items-center gap-2 px-7 py-3.5 rounded-full border border-rogue-cyan/30 text-rogue-cyan font-semibold text-sm hover:bg-rogue-cyan/10 hover:border-rogue-cyan/60 transition-all duration-250"
            >
              <Play size={14} className="fill-rogue-cyan" />
              View live demo
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
